import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  triplePSDatasource,
} from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { ApiKey, CreatedApiKey } from "../../models";
import { logout } from "../Auth/authSlice";

export interface ApiKeysState {
  getApiKeys: APIResult<ApiKey[]>;
  createApiKey: APIResult<CreatedApiKey>;
  updateApiKey: APIResult<ApiKey>;
  revokeApiKey: APIResult<{ id: string; revokedAt: string }>;

  apiKeys: ApiKey[];
  createdKey?: CreatedApiKey;
  showCreateModal: boolean;
  editingKey?: ApiKey;
  revokingKey?: ApiKey;
}

const initialState: ApiKeysState = {
  getApiKeys: emptyAPIResult(),
  createApiKey: emptyAPIResult(),
  updateApiKey: emptyAPIResult(),
  revokeApiKey: emptyAPIResult(),

  apiKeys: [],
  showCreateModal: false,
};

export const getApiKeys = createAsyncThunk("apiKeys/getApiKeys", async () => {
  return triplePSDatasource.getApiKeys();
});

export const createApiKey = createAsyncThunk(
  "apiKeys/createApiKey",
  async (req: CreateApiKeyRequest, thunkAPI) => {
    const created = await triplePSDatasource.createApiKey(req);
    // Refetch so the new key shows in the list immediately (with its server
    // id + timestamps), rather than only after a manual refresh.
    await thunkAPI.dispatch(getApiKeys());
    return created;
  }
);

export const updateApiKey = createAsyncThunk(
  "apiKeys/updateApiKey",
  async ({ id, req }: { id: string; req: UpdateApiKeyRequest }, thunkAPI) => {
    const updated = await triplePSDatasource.updateApiKey(id, req);
    // Refetch so the row reflects server truth (the PUT response omits id).
    await thunkAPI.dispatch(getApiKeys());
    return updated;
  }
);

export const revokeApiKey = createAsyncThunk(
  "apiKeys/revokeApiKey",
  async (id: string, thunkAPI) => {
    await triplePSDatasource.revokeApiKey(id);
    // Refetch so the revoked status reflects server truth.
    await thunkAPI.dispatch(getApiKeys());
    // 204 has no body; stamp the revocation time client-side for display.
    return { id, revokedAt: new Date().toISOString() };
  }
);

export const apiKeysSlice = createSlice({
  name: "apiKeys",
  initialState,
  reducers: {
    openCreateModal(state) {
      state.showCreateModal = true;
      state.createApiKey = emptyAPIResult();
    },
    closeCreateModal(state) {
      state.showCreateModal = false;
      state.createApiKey = emptyAPIResult();
    },
    closeRevealModal(state) {
      state.createdKey = undefined;
      state.createApiKey = emptyAPIResult();
    },
    setEditingKey(state, action: PayloadAction<ApiKey | undefined>) {
      state.editingKey = action.payload;
      state.updateApiKey = emptyAPIResult();
    },
    setRevokingKey(state, action: PayloadAction<ApiKey | undefined>) {
      state.revokingKey = action.payload;
      state.revokeApiKey = emptyAPIResult();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApiKeys.pending, (state) => {
        state.getApiKeys = loadingAPIResult();
      })
      .addCase(getApiKeys.fulfilled, (state, action) => {
        state.getApiKeys = successAPIResult(action.payload);
        state.apiKeys = action.payload;
      })
      .addCase(getApiKeys.rejected, (state, action) => {
        state.getApiKeys = errorAPIResult(action.error);
      })
      .addCase(createApiKey.pending, (state) => {
        state.createApiKey = loadingAPIResult();
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.createApiKey = successAPIResult(action.payload);
        state.createdKey = action.payload;
        state.showCreateModal = false;
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.createApiKey = errorAPIResult(action.error);
      })
      .addCase(updateApiKey.pending, (state) => {
        state.updateApiKey = loadingAPIResult();
      })
      .addCase(updateApiKey.fulfilled, (state, action) => {
        state.updateApiKey = successAPIResult(action.payload);
        const idx = state.apiKeys.findIndex((k) => k.id === action.payload.id);
        if (idx !== -1) state.apiKeys[idx] = action.payload;
        state.editingKey = undefined;
      })
      .addCase(updateApiKey.rejected, (state, action) => {
        state.updateApiKey = errorAPIResult(action.error);
      })
      .addCase(revokeApiKey.pending, (state) => {
        state.revokeApiKey = loadingAPIResult();
      })
      .addCase(revokeApiKey.fulfilled, (state, action) => {
        state.revokeApiKey = successAPIResult(action.payload);
        const idx = state.apiKeys.findIndex((k) => k.id === action.payload.id);
        if (idx !== -1) state.apiKeys[idx].revokedAt = action.payload.revokedAt;
        state.revokingKey = undefined;
      })
      .addCase(revokeApiKey.rejected, (state, action) => {
        state.revokeApiKey = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getApiKeys = initialState.getApiKeys;
        state.createApiKey = initialState.createApiKey;
        state.updateApiKey = initialState.updateApiKey;
        state.revokeApiKey = initialState.revokeApiKey;

        state.apiKeys = initialState.apiKeys;
        state.createdKey = undefined;
        state.showCreateModal = false;
        state.editingKey = undefined;
        state.revokingKey = undefined;
      });
  },
});

export const {
  openCreateModal,
  closeCreateModal,
  closeRevealModal,
  setEditingKey,
  setRevokingKey,
} = apiKeysSlice.actions;

export default apiKeysSlice.reducer;
